import React, { useEffect, useState } from 'react';

import classNames from 'classnames';

import { useProcurement } from '../procurement/context/ProcurementContext';

import { formatToFixedSeparateThousands } from 'Helpers/formatService';
import { apiClient } from 'Helpers/network';

import BulkDeleteButtons from 'CommonComponents/BulkDeleteButtons';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import PerPageSelect from 'CommonComponents/PerPageSelect';
import TablePagination from 'CommonComponents/TablePagination';
import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';
import TableSearchInput from 'CommonComponents/TableSearchInput';
import TextButton from 'CommonComponents/TextButton';

import useApiRequest from 'Hooks/useApiRequest';
import useBulkDelete from 'Hooks/useBulkDelete';
import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import usePagination from 'Hooks/usePagination';
import useQuery from 'Hooks/useQuery';
import { useToast } from 'Hooks/useToast';

import EyeIcon from 'Assets/icons/eye.svg';
import TrashIcon from 'Assets/icons/trash.svg';

export default function WorkforceContractsIndexTable(props) {
    const [cancelContract, setCancelContract] = useState({});
    const [deleteContract, setDeleteContract] = useState({});
    const [searchValue, setSearchValue] = useState('');

    const { routes, settings } = useProcurement();

    const pagination = usePagination({ perPage: 25 });

    const toast = useToast();

    const { data: contracts, get: fetchContracts, loading: fetchingContacts } = useApiRequest({ url: props.fetchUrl });

    const { toggleSelecting, selecting, selectedItems, toggleItem } = useBulkDelete();

    const bulkDeleteContractsRequest = useQuery({
        enabled: false,
        queryFn: ({ ids, all }) => apiClient.delete(routes.workforceContractBulkDeleteUrl, { data: { ids, all } }),
        successFn: () => {
            toast.success('Items removed successfully');
            props.triggerRerender();
            toggleSelecting();
        },
    });

    const confirm = useConfirmationModal();

    useEffect(() => {
        getContracts();
    }, [pagination.page, pagination.perPage, searchValue]);

    useEffect(() => {
        if (contracts) {
            const { meta } = contracts;
            pagination.setTotal(meta.total);
            pagination.setLastPage(meta.last_page);
            pagination.setFrom(meta.from);
            pagination.setTo(meta.to);
        }
    }, [contracts]);

    useEffect(() => {
        if (Object.keys(cancelContract).length > 0) {
            confirm({
                title: $t('Delete Contract'),
                message: $t('Are you sure you want to delete the commitment made against Employee Number {{ref}}?', {
                    ref: cancelContract?.contract_reference,
                }),
                warning: $t(
                    'The commitment and budget linked to this position will be deleted, however, the position will remain and you can add the budget back by editing the position and allocating a pay rate against it.',
                ),
                onConfirm: () => triggerCancelContract(),
                onCancel: () => {
                    setCancelContract({});
                },
                onClose: () => {
                    setCancelContract({});
                },
            });
        }
    }, [cancelContract]);

    useEffect(() => {
        if (Object.keys(deleteContract).length > 0) {
            confirm({
                title: $t('Delete Contract'),
                message: $t('Are you sure you want to permanently delete contract {{ref}}?', { ref: deleteContract?.contract_reference }),
                onConfirm: () => triggerDeleteContract(),
                onCancel: () => {
                    setDeleteContract({});
                },
                onClose: () => {
                    setDeleteContract({});
                },
            });
        }
    }, [deleteContract]);

    const getContracts = (params) => {
        fetchContracts({ cancelled_contracts: !!props.forCancel, per_page: pagination.perPage, page: pagination.page, search: searchValue, ...params });
    };

    const onBulkDeleteContracts = () => {
        confirm({
            title: $t('Delete Contracts'),
            message: selecting ? $t('Are you sure you want to delete the selected contracts?') : $t('Are you sure you want to delete all contracts?'),
            warning: $t('Deleting a contract will also delete commitments linked to this contract'),
            onConfirm: () => triggerBulkDeleteContracts(),
        });
    };

    const triggerBulkDeleteContracts = () => {
        let ids = selectedItems;

        if (selecting && !selectedItems.length) {
            return;
        }

        bulkDeleteContractsRequest.refetch({ ids, all: !selecting });
    };

    const triggerContractAction = (contract) => {
        props.forCancel ? setDeleteContract(contract) : setCancelContract(contract);
    };

    const sendDeleteContractRequest = (deleteContractUrl) => {
        axios
            .delete(deleteContractUrl)
            .then(() => {
                props.triggerRerender();
            })
            .catch(() => toast.error($t('An error occurred, please try again')))
            .finally(() => toast.success($t('Contract {{ref}} deleted successfully', { ref: deleteContract?.contract_reference })));
    };

    const triggerCancelContract = () => {
        const url = props.workforceContractDeleteUrl.replace('CONTRACT_ID', cancelContract?.id);
        sendDeleteContractRequest(url);
    };

    const triggerDeleteContract = () => {
        const url = props.workforceContractDeleteUrl.replace('CONTRACT_ID', deleteContract?.id);
        sendDeleteContractRequest(url);
    };

    return (
        <div className="table__card">
            <div className="tw-flex tw-justify-between tw-mb-6">
                <div className="tw-flex tw-flex-col tw-gap-y-1">
                    <h3 className="table__header">{props.forCancel ? $t('Deleted Workforce Contracts') : $t('Workforce Contracts')}</h3>
                    <div className="table__pagination-info">
                        <PaginationInfo to={pagination.to} from={pagination.from} total={pagination.total} />
                        <TablePagination
                            lastPage={pagination.lastPage}
                            pageChange={(data) => pagination.setPage(+data.selected + 1)}
                            forcePage={pagination.page - 1}
                        />
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-space-x-2">
                    <div>
                        <PerPageSelect
                            value={pagination.perPage}
                            onChange={(e) => {
                                pagination.setPerPage(parseInt(e.target.value));
                            }}
                        />
                    </div>
                    <div>
                        <TableSearchInput searchValue={searchValue} handleEnterPress={(e, value) => setSearchValue(value)} />
                    </div>
                    <BulkDeleteButtons
                        selecting={selecting}
                        selectedCount={selectedItems.length}
                        itemsCount={contracts?.data?.length || 0}
                        loading={bulkDeleteContractsRequest.loading}
                        onDelete={() => onBulkDeleteContracts()}
                        toggleSelecting={toggleSelecting}
                    />
                </div>
            </div>

            <div className="inbud-table_wrapper">
                <table className="inbud-table">
                    <thead>
                        <tr>
                            <th rowSpan={props.forDeletedContracts ? 1 : 2} className={classNames('tw-w-8', { 'tw-hidden': !selecting })}>
                                {!!selectedItems.length && (
                                    <div className="tw-rounded-full tw-h-4 tw-w-4 tw-flex tw-items-center tw-justify-center tw-text-2xs tw-text-white tw-bg-red-75">
                                        {selectedItems.length}
                                    </div>
                                )}
                            </th>
                            <th className={'tw-min-w-32'}>{$t('Employee Number')}</th>
                            <th className={'tw-min-w-48'}>{$t('Department/Costcenter')}</th>
                            <th className={'tw-min-w-48'}>{$t('Position')}</th>
                            <th className={'tw-min-w-32'}>{$t('Name')}</th>
                            <th className={'tw-min-w-32'}>{$t('Total')}</th>
                            <th className={'tw-min-w-32'}>{$t('Actual Spend')}</th>
                            <th className={'tw-min-w-32'}>{$t('Budget Code')}</th>
                            <th className={'tw-min-w-32'}>{$t('Actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {fetchingContacts ? (
                            <TableRowSkeleton rows={10} columns={8} />
                        ) : (
                            contracts?.data?.map((contract) => {
                                return (
                                    <tr key={contract.id}>
                                        {/* Hidden checkbox cell */}
                                        <td className={classNames({ 'tw-hidden': !selecting })}>
                                            <input
                                                type="checkbox"
                                                className="input-checkbox"
                                                checked={selectedItems.includes(contract.id)}
                                                onChange={() => toggleItem(contract.id)}
                                            />
                                        </td>
                                        <td>{contract.contract_reference ? contract.contract_reference : '-'}</td>
                                        <td>
                                            {contract.department_name} {!!contract.costcenter_name && <span> / {contract.costcenter_name}</span>}
                                        </td>
                                        <td>{contract.supervisor_name || '-'}</td>
                                        <td>
                                            {contract.name} {contract.last_name}
                                        </td>
                                        <td className="inbud-numeric-cell">{formatToFixedSeparateThousands(contract.cashflow_total, ',')}</td>
                                        <td className="inbud-numeric-cell">{formatToFixedSeparateThousands(contract.actual_spend, ',')}</td>
                                        <td>{contract.supervisor_reference_number || '-'}</td>
                                        <td>
                                            <div className="tw-flex tw-items-center tw-gap-x-3">
                                                {settings.isSupplierPortalEnabled && !!contract.settings_portal_url && (
                                                    <TextButton variant="primary" onClick={() => (window.location.href = contract.settings_portal_url)}>
                                                        <EyeIcon className="tw-w-3 tw-h-3" /> {$t('Portal Settings')}
                                                    </TextButton>
                                                )}
                                                <TextButton variant="danger" onClick={() => triggerContractAction(contract)}>
                                                    <TrashIcon className="tw-w-3 tw-h-3" /> {$t('Delete Contract')}
                                                </TextButton>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}

                        {!fetchingContacts && !contracts?.data?.length && (
                            <tr>
                                <td colSpan={8}>{$t('No workforce contracts')}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
